import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FilePdf, FloppyDisk, PencilSimple, Truck, Package, CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_LABEL = {
  draft: { l: "Borrador", cls: "bg-slate-200 text-slate-700" },
  sent: { l: "Enviada", cls: "bg-amber-200 text-amber-800" },
  partial: { l: "Parcial", cls: "bg-orange-200 text-orange-800" },
  received: { l: "Recibida", cls: "bg-emerald-200 text-emerald-800" },
  closed: { l: "Cerrada", cls: "bg-sky-200 text-sky-800" },
  cancelled: { l: "Cancelada", cls: "bg-rose-200 text-rose-800" },
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// -------- PO form dialog --------
function POFormDialog({ initial, items, onClose, onSaved }) {
  const [po, setPo] = useState(initial);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setPo((s) => ({ ...s, [k]: v }));
  const updLine = (idx, k, v) => setPo((s) => ({
    ...s, items: s.items.map((it, i) => i === idx ? { ...it, [k]: k === "qty" || k === "unit_price" ? parseFloat(v) || 0 : v } : it)
  }));
  const addLine = () => setPo((s) => ({ ...s, items: [...s.items, { item_id: "", qty: 0, unit_price: 0, notes: "" }] }));
  const delLine = (idx) => setPo((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    const sub = po.items.reduce((a, i) => a + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);
    const iva = sub * (parseFloat(po.iva_pct) || 0) / 100;
    return { subtotal: sub, iva, total: sub + iva };
  }, [po.items, po.iva_pct]);

  const save = async () => {
    if (!po.supplier.trim()) { toast.error("Indica el proveedor"); return; }
    if (po.items.length === 0 || !po.items.some((i) => i.item_id && i.qty > 0)) {
      toast.error("Añade al menos un ítem con cantidad > 0"); return;
    }
    setBusy(true);
    try {
      const clean = { ...po, items: po.items.filter((i) => i.item_id && i.qty > 0) };
      const r = po.id ? await api.put(`/purchases/pos/${po.id}`, clean) : await api.post("/purchases/pos", clean);
      toast.success(`OC ${r.data.po_number} guardada`);
      onSaved(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const onItemChange = (idx, itemId) => {
    const inv = items.find((x) => x.id === itemId);
    setPo((s) => ({
      ...s, items: s.items.map((it, i) => i === idx ? {
        ...it, item_id: itemId, item_code: inv?.code || "", description: inv?.description || "",
        unit: inv?.unit || "", unit_price: it.unit_price || inv?.unit_cost || 0,
      } : it)
    }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">
          {po.id ? "Editar" : "Nueva"} Orden de Compra {po.po_number && <span className="text-xs text-slate-500 font-mono ml-2">· {po.po_number}</span>}
        </DialogTitle></DialogHeader>

        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="overline-label">Proveedor *</Label>
            <Input value={po.supplier} onChange={(e) => upd("supplier", e.target.value)} data-testid="po-supplier" /></div>
          <div><Label className="overline-label">RFC</Label>
            <Input value={po.supplier_rfc || ""} onChange={(e) => upd("supplier_rfc", e.target.value)} /></div>
          <div><Label className="overline-label">Contacto</Label>
            <Input value={po.supplier_contact || ""} onChange={(e) => upd("supplier_contact", e.target.value)} /></div>
          <div><Label className="overline-label">Fecha OC</Label>
            <Input type="date" value={po.date} onChange={(e) => upd("date", e.target.value)} data-testid="po-date" /></div>
          <div><Label className="overline-label">Entrega esperada</Label>
            <Input type="date" value={po.expected_delivery || ""} onChange={(e) => upd("expected_delivery", e.target.value)} /></div>
          <div><Label className="overline-label">IVA %</Label>
            <Input type="number" step="0.01" value={po.iva_pct} onChange={(e) => upd("iva_pct", parseFloat(e.target.value) || 0)} /></div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading font-bold text-slate-700 uppercase text-xs tracking-widest">Ítems</h4>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line" className="h-8 gap-1"><Plus size={12} /> Añadir</Button>
          </div>
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100">
                <tr className="text-left text-slate-500 uppercase tracking-wider">
                  <th className="px-2 py-1 font-heading">Ítem</th>
                  <th className="px-2 py-1 font-heading text-right">Cant.</th>
                  <th className="px-2 py-1 font-heading text-right">P.Unit.</th>
                  <th className="px-2 py-1 font-heading text-right">Importe</th>
                  <th className="px-2 py-1 font-heading text-right">Recibido</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {po.items.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-400 italic">Sin ítems.</td></tr>}
                {po.items.map((it, idx) => {
                  const sub = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                  return (
                    <tr key={idx} data-testid={`po-line-${idx}`} className="border-b border-slate-100">
                      <td className="px-2 py-1">
                        <Select value={it.item_id || "__none__"} onValueChange={(v) => onItemChange(idx, v === "__none__" ? "" : v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`po-item-${idx}`}><SelectValue placeholder="Elegir ítem…" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-72 overflow-y-auto">
                            <SelectItem value="__none__">— Seleccionar —</SelectItem>
                            {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} · {i.description}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1 w-24"><Input type="number" step="0.01" value={it.qty} onChange={(e) => updLine(idx, "qty", e.target.value)}
                        className="h-8 text-xs text-right font-mono" data-testid={`po-qty-${idx}`} /></td>
                      <td className="px-2 py-1 w-28"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updLine(idx, "unit_price", e.target.value)}
                        className="h-8 text-xs text-right font-mono" data-testid={`po-price-${idx}`} /></td>
                      <td className="px-2 py-1 text-right font-bold text-slate-800">{money(sub)}</td>
                      <td className="px-2 py-1 text-right text-slate-500">{it.received_qty ? `${it.received_qty} ${it.unit || ""}` : "—"}</td>
                      <td className="px-2 py-1 w-8"><button type="button" onClick={() => delLine(idx)} className="text-slate-400 hover:text-red-600"><Trash size={13} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3 bg-slate-50 rounded p-3 grid grid-cols-3 gap-3 text-xs">
          <div><span className="text-slate-500">Subtotal</span><br/><b className="text-sm">{money(totals.subtotal)}</b></div>
          <div><span className="text-slate-500">IVA ({po.iva_pct}%)</span><br/><b className="text-sm">{money(totals.iva)}</b></div>
          <div><span className="text-blue-900 uppercase font-bold">TOTAL</span><br/><b className="text-lg font-heading text-blue-900" data-testid="po-total">{money(totals.total)}</b></div>
        </div>

        <div><Label className="overline-label mt-3 block">Notas</Label>
          <Textarea rows={2} value={po.notes || ""} onChange={(e) => upd("notes", e.target.value)} /></div>

        <div className="flex justify-end gap-2 mt-4 sticky bottom-0 bg-white pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {po.id && (
            <Button variant="outline" onClick={async () => {
              try { const res = await api.get(`/purchases/pos/${po.id}/pdf`, { responseType: "blob" });
                downloadFile(res.data, `${po.po_number}.pdf`); }
              catch (e) { toast.error(apiError(e)); }
            }} className="gap-1.5" data-testid="po-pdf">
              <FilePdf size={14} weight="bold" /> PDF
            </Button>
          )}
          <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="po-save">
            <FloppyDisk size={14} weight="bold" /> {busy ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Reception dialog --------
function ReceiveDialog({ po, onClose, onReceived }) {
  const pending = po.items.filter((it) => (parseFloat(it.qty) - parseFloat(it.received_qty || 0)) > 0.001);
  const [lines, setLines] = useState(pending.map((it) => ({
    po_item_id: it.id, item_id: it.item_id, item_code: it.item_code,
    description: it.description, unit: it.unit,
    remaining: parseFloat(it.qty) - parseFloat(it.received_qty || 0),
    qty_received: 0, unit_cost: it.unit_price, lot_ref: "", notes: "",
  })));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [deliveryNote, setDeliveryNote] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const updL = (idx, k, v) => setLines((s) => s.map((l, i) => i === idx ? { ...l, [k]: (k === "qty_received" || k === "unit_cost") ? parseFloat(v) || 0 : v } : l));

  const submit = async () => {
    const items = lines.filter((l) => l.qty_received > 0).map((l) => ({
      po_item_id: l.po_item_id, item_id: l.item_id,
      qty_received: l.qty_received, unit_cost: l.unit_cost,
      lot_ref: l.lot_ref, notes: l.notes,
    }));
    if (items.length === 0) { toast.error("Indica al menos una cantidad recibida"); return; }
    setBusy(true);
    try {
      await api.post(`/purchases/pos/${po.id}/receive`, { date, delivery_note: deliveryNote, items, notes });
      toast.success(`Recepción registrada — ${items.length} línea(s) ingresadas al inventario (WAC recalculado)`);
      onReceived();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
          <Truck size={22} weight="fill" className="text-emerald-700" /> Recibir mercancía · OC {po.po_number}
        </DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Al confirmar, se generan movimientos <b>entrada</b> en inventario y se recalcula el <b>Costo Promedio Ponderado (WAC)</b> por ítem. También se crean lotes trazables para FIFO.</p>

        <div className="grid md:grid-cols-3 gap-3 mt-3">
          <div><Label className="overline-label">Fecha</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rcp-date" /></div>
          <div className="md:col-span-2"><Label className="overline-label">Nota de entrega / factura</Label>
            <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="REM-2026-1801" data-testid="rcp-delivery-note" /></div>
        </div>

        <div className="mt-3 border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr className="text-left text-slate-500 uppercase tracking-wider">
                <th className="px-2 py-1 font-heading">Ítem</th>
                <th className="px-2 py-1 font-heading text-right">Pendiente</th>
                <th className="px-2 py-1 font-heading text-right">Recibiendo</th>
                <th className="px-2 py-1 font-heading text-right">Costo u.</th>
                <th className="px-2 py-1 font-heading">Lote proveedor</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-slate-400 italic">La OC ya está totalmente recibida.</td></tr>}
              {lines.map((l, idx) => (
                <tr key={idx} data-testid={`rcp-line-${idx}`} className="border-b border-slate-100">
                  <td className="px-2 py-1">
                    <div className="font-mono font-bold">{l.item_code}</div>
                    <div className="text-slate-500 text-[10px]">{l.description}</div>
                  </td>
                  <td className="px-2 py-1 text-right font-bold text-amber-700">{l.remaining} {l.unit}</td>
                  <td className="px-2 py-1 w-32"><Input type="number" step="0.01" max={l.remaining} value={l.qty_received}
                    onChange={(e) => updL(idx, "qty_received", e.target.value)}
                    className={`h-8 text-xs text-right font-mono ${l.qty_received > l.remaining ? "border-red-500" : ""}`} data-testid={`rcp-qty-${idx}`} /></td>
                  <td className="px-2 py-1 w-28"><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => updL(idx, "unit_cost", e.target.value)}
                    className="h-8 text-xs text-right font-mono" data-testid={`rcp-cost-${idx}`} /></td>
                  <td className="px-2 py-1"><Input value={l.lot_ref} onChange={(e) => updL(idx, "lot_ref", e.target.value)}
                    placeholder="opcional" className="h-8 text-xs" data-testid={`rcp-lot-${idx}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3"><Label className="overline-label">Observaciones</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rcp-notes" /></div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || lines.length === 0} className="bg-emerald-700 hover:bg-emerald-600 gap-1.5" data-testid="rcp-confirm">
            <CheckCircle size={14} weight="bold" /> {busy ? "Registrando…" : "Confirmar recepción"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Main tab --------
export default function PurchaseOrdersTab({ items, canWrite, canReports }) {
  const [pos, setPos] = useState([]);
  const [editingPo, setEditingPo] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    const q = filterStatus === "all" ? "" : `?status=${filterStatus}`;
    try { const r = await api.get(`/purchases/pos${q}`); setPos(r.data || []); }
    catch (e) { toast.error(apiError(e)); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditingPo({
    supplier: "", supplier_rfc: "", supplier_contact: "",
    date: new Date().toISOString().slice(0, 10), expected_delivery: "",
    iva_pct: 16, currency: "MXN", status: "draft", items: [],
    notes: "",
  });

  const removePo = async (id) => {
    if (!window.confirm("¿Cancelar o eliminar esta OC?")) return;
    try { await api.delete(`/purchases/pos/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="po-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="overline-label">Filtrar estado:</Label>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-52" data-testid="po-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {canWrite && (
          <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="po-new">
            <Plus size={14} weight="bold" /> Nueva OC
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2 font-heading">OC</th>
              <th className="px-3 py-2 font-heading">Proveedor</th>
              <th className="px-3 py-2 font-heading">Fecha</th>
              <th className="px-3 py-2 font-heading">Entrega</th>
              <th className="px-3 py-2 font-heading text-right">Total</th>
              <th className="px-3 py-2 font-heading">Progreso</th>
              <th className="px-3 py-2 font-heading">Estado</th>
              <th className="px-3 py-2 font-heading text-right"></th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Sin órdenes de compra.</td></tr>}
            {pos.map((p) => {
              const totalQty = (p.items || []).reduce((a, i) => a + parseFloat(i.qty || 0), 0);
              const recQty = (p.items || []).reduce((a, i) => a + parseFloat(i.received_qty || 0), 0);
              const pct = totalQty > 0 ? Math.round((recQty / totalQty) * 100) : 0;
              const st = STATUS_LABEL[p.status] || STATUS_LABEL.draft;
              const canReceive = ["sent", "partial"].includes(p.status);
              return (
                <tr key={p.id} data-testid={`po-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono font-bold text-amber-800">{p.po_number}</td>
                  <td className="px-3 py-2 text-slate-700">{p.supplier}</td>
                  <td className="px-3 py-2 text-slate-500">{p.date}</td>
                  <td className="px-3 py-2 text-slate-500">{p.expected_delivery || "-"}</td>
                  <td className="px-3 py-2 text-right font-bold text-blue-900">{money(p.totals?.total || 0)}</td>
                  <td className="px-3 py-2">
                    <div className="w-24 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                      <div className={`h-full ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-orange-500" : "bg-slate-400"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-500">{pct}%</span>
                  </td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${st.cls}`}>{st.l}</span></td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex gap-2 justify-end">
                      {canWrite && canReceive && (
                        <button title="Recibir mercancía" onClick={() => setReceiving(p)} className="text-emerald-700 hover:text-emerald-900" data-testid={`po-receive-${p.id}`}>
                          <Truck size={15} weight="bold" />
                        </button>
                      )}
                      {canWrite && (
                        <button title="Editar" onClick={() => setEditingPo(p)} className="text-slate-400 hover:text-blue-900" data-testid={`po-edit-${p.id}`}>
                          <PencilSimple size={15} />
                        </button>
                      )}
                      {canReports && (
                        <button title="PDF" onClick={async () => {
                          try { const res = await api.get(`/purchases/pos/${p.id}/pdf`, { responseType: "blob" });
                            downloadFile(res.data, `${p.po_number}.pdf`); }
                          catch (e) { toast.error(apiError(e)); }
                        }} className="text-slate-400 hover:text-amber-700"><FilePdf size={15} /></button>
                      )}
                      {canWrite && (
                        <button title="Cancelar / eliminar" onClick={() => removePo(p.id)} className="text-slate-400 hover:text-red-600" data-testid={`po-delete-${p.id}`}>
                          <XCircle size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingPo && <POFormDialog initial={editingPo} items={items}
        onClose={() => setEditingPo(null)}
        onSaved={() => { setEditingPo(null); load(); window.dispatchEvent(new Event("inventory-updated")); }} />}
      {receiving && <ReceiveDialog po={receiving}
        onClose={() => setReceiving(null)}
        onReceived={() => { setReceiving(null); load(); window.dispatchEvent(new Event("inventory-updated")); }} />}
    </div>
  );
}
